<template>
  <div>
    <div
      class="rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors"
      :class="
        dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
      "
      :data-dragging="dragging"
      data-cy="file-dropzone-area"
      @dragenter.prevent="onDragOver"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <i class="fas fa-file-upload text-2xl text-gray-400" aria-hidden="true" />
      <p class="mt-3 hidden text-sm text-gray-700 md:block">
        Drag a file here, or
      </p>
      <button
        type="button"
        class="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
        :disabled="disabled"
        data-cy="file-dropzone-choose"
        @click="input?.click()"
      >
        <i class="fas fa-upload" aria-hidden="true" />
        Choose file
      </button>
      <input
        ref="input"
        type="file"
        class="hidden"
        :accept="acceptAttr"
        :disabled="disabled"
        data-cy="file-dropzone-input"
        @change="onChange"
      />
      <p
        v-if="hint"
        class="mt-3 text-xs text-gray-500"
        data-cy="file-dropzone-hint"
      >
        {{ hint }}
      </p>
    </div>
    <p
      v-if="shownError"
      class="mt-2 text-sm text-red-600"
      role="alert"
      data-cy="file-dropzone-error"
    >
      {{ shownError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { normalizeExtensions } from "./fileExtensions";

const props = withDefaults(
  defineProps<{
    /** Allowed extensions, with or without the leading dot. */
    accept?: string[];
    hint?: string;
    disabled?: boolean;
    error?: string;
  }>(),
  { accept: () => [], hint: "", disabled: false, error: "" }
);

const emit = defineEmits<{ file: [file: File] }>();

const input = ref<HTMLInputElement | null>(null);
const dragging = ref(false);
const typeError = ref("");

const extensions = computed(() => normalizeExtensions(props.accept));
const acceptAttr = computed(() => extensions.value.join(","));
const shownError = computed(() => props.error || typeError.value);

// Dropped files skip the input's `accept`, so the extension is checked here too.
function pick(file: File | undefined) {
  typeError.value = "";
  if (!file || props.disabled) return;
  const name = file.name.toLowerCase();
  if (
    extensions.value.length &&
    !extensions.value.some((ext) => name.endsWith(ext))
  ) {
    typeError.value = `Use a ${extensions.value.join(" or ")} file`;
    return;
  }
  emit("file", file);
}

function onChange(event: Event) {
  const target = event.target as HTMLInputElement;
  pick(target.files?.[0]);
  target.value = "";
}

function onDragOver(event: DragEvent) {
  if (props.disabled) return;
  dragging.value = true;
  if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
}

// Moving onto a child fires dragleave on the area; only clear when the pointer really left it.
function onDragLeave(event: DragEvent) {
  const area = event.currentTarget as Node;
  if (!area.contains(event.relatedTarget as Node | null))
    dragging.value = false;
}

function onDrop(event: DragEvent) {
  dragging.value = false;
  pick(event.dataTransfer?.files?.[0]);
}
</script>
